import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToasterService } from '../../../services/toaster.service';
import { Subscription } from 'rxjs';
import { concatMap, tap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { InputLabelComponent } from '../../../components/utility/form/input-label/input-label.component';
import { TextInputComponent } from '../../../components/utility/form/text-input/text-input.component';
import { regex } from '../../../constants/regex';
import { UtilityService } from '../../../services/utility.service';
import { CheckboxInputComponent } from '../../../components/utility/form/checkbox-input/checkbox-input.component';
import { UploadInputComponent } from '../../../components/utility/form/upload-input/upload-input.component';
import { ApiService } from '../../../services/api.service';
import { UserProfileDetail } from '../../../models/user-profile.model';
import { unsubscribe } from '../../../decorators/unsubscribe.decorator';
import { FileObject } from '../../../models/file-object.model';
import { AuthService } from '../../../services/auth.service';
import { routes } from '../../../constants/routes';
import { ApiErrorHandler } from '../../../models/api-error-handler.model';
import { apiErrorHandler } from '../../../constants/api-error-handler';
import { MatDialog } from '@angular/material/dialog';
import { pageSeoInfo } from '../../../constants/page-seo-info';
import { SeoService } from '../../../services/seo.service';
import { DomSanitizer } from '@angular/platform-browser';
import { ConfirmationDialogActionComponent } from '../../../components/utility/confirmation-dialog-action/confirmation-dialog-action.component';
import { EmailSelectionDialogComponent } from '../../../components/utility/email-selection-dialog/email-selection-dialog.component';

@Component({
  selector: 'app-my-profile',
  standalone: true,
  imports: [
    TextInputComponent,
    ReactiveFormsModule,
    InputLabelComponent,
    CheckboxInputComponent,
    UploadInputComponent,
  ],
  templateUrl: './my-profile.component.html',
  styleUrl: './my-profile.component.scss',
})
@unsubscribe()
export class MyProfileComponent implements OnInit, OnDestroy, ApiErrorHandler {
  public myProfileForm!: FormGroup;
  resetInput = false;

  isEmailVerifiable: boolean = false;
  isEmailVerified: string = '';
  verifiedEmailValue: string = '';

  private updateUserDataSub!: Subscription;

  public profileImage?: string = '';
  public isUserNameVerified: string | boolean = '';
  private imageProfileData?: FileObject;
  private userDetails!: any;
  // : UserProfileDetail;
  isProfilePicUploaded: boolean = false;

  readonly profileFormValidations = {
    name: {
      maxLength: 50,
    },
    mobileNo: {
      maxLength: 10,
    },
    email: {
      maxLength: 150,
    },
  };

  readonly userNameKeypressFn = () => {
    this.isUserNameVerified = false;
    return true;
  };

  constructor(
    private router: Router,
    private matDialog: MatDialog,
    public toaster: ToasterService,
    private apiService: ApiService,
    private authService: AuthService,
    private sanitizer: DomSanitizer,
    public utilityService: UtilityService,
    private activatedRoute: ActivatedRoute,
    private seoService: SeoService,
  ) {
    this.userDetails = this.activatedRoute.snapshot.data['profileDetails'];
  }

  ngOnInit() {
    this.initProfileForm();
    this.prefillUserDetails();
    const { title, description, canonicalUrl } = pageSeoInfo['profile'];
    this.seoService.setPageTitle(title);
    this.seoService.setPageDescription(description);
    this.seoService.setCanonicalUrl(canonicalUrl);
    if (
      this.userDetails?.provider === 'JanParichay' &&
      this.userDetails?.email
    ) {
      this.myProfileForm?.get('email').setValue(this.userDetails?.email);
      this.myProfileForm?.get('email').disable();
      this.isEmailVerifiable = false;
      this.isEmailVerified = '/images/success.svg';
      this.verifiedEmailValue = this.userDetails?.email;
    }
  }

  private initProfileForm() {
    this.myProfileForm = new FormGroup({
      username: new FormControl(
        {
          value: this.userDetails?.username || '',
          disabled: !!this.userDetails?.username,
        },
        [
          Validators.pattern(regex.userNameWithNoSpecialChar),
          Validators.required,
          Validators.minLength(6),
        ],
      ),
      fullName: new FormControl(
        {
          value: this.userDetails?.name || '',
          disabled: !!this.userDetails?.name,
        },
        [Validators.required],
      ),
      profilePicture: new FormControl(''),
      phone: new FormControl(
        {
          value: this.userDetails?.phone,
          disabled: !!this.userDetails?.phone,
        },
        [Validators.required, Validators.pattern(regex.mobileNumber)],
      ),
      email: new FormControl(
        { value: this.userDetails?.email, disabled: !!this.userDetails?.email },
        [
          Validators.required,
          Validators.email,
          Validators.pattern(regex.email),
        ],
      ),

      isAgreeTnc: new FormControl('', [Validators.requiredTrue]),
    });
  }

  prefillUserDetails() {
    const profile = this.userDetails;
    this.myProfileForm.patchValue({
      profilePicture: profile?.profilePic,
    });
  }

  onProfileUpload(imageProfileData: FileObject) {
    this.isProfilePicUploaded = true;
    this.resetInput = false;
    this.imageProfileData = Array.isArray(imageProfileData)
      ? imageProfileData[0]
      : imageProfileData;
    this.profileImage = this.imageProfileData.base64;
  }

  onProfileImageRemove() {
    this.isProfilePicUploaded = false;
    this.imageProfileData = undefined;
    this.profileImage = undefined;
    this.resetInput = true;
    this.myProfileForm.get('profilePicture')?.reset();
  }

  onInputBlur(controlName: 'email' | 'username') {
    const control = this.myProfileForm.get(controlName);

    if (control?.invalid) {
      this.updateVerificationStatus(controlName, false);
      return;
    }

    if (controlName === 'email') {
      this.isEmailVerifiable = false;
      this.checkIfExist(control.value, controlName);
    } else if (controlName === 'username') {
      this.checkIfExist(control.value, controlName);
      if (this.userDetails?.username === control.value) {
        this.isUserNameVerified = '/images/success.svg';
        return;
      } else {
        this.isUserNameVerified = '';
      }
    }
  }

  checkIfExist(value: string, controlName: 'email' | 'username') {
    this.apiService
      .checkUsernameAndEmailExist(value, controlName, true)
      .pipe(
        tap((resp) => {
          const shouldReturn = typeof resp === 'boolean' ? resp : resp?.message;
          if (resp && controlName === 'username') {
            return;
          }
          if (shouldReturn && controlName === 'email') {
            return;
          }
          if (controlName === 'email') {
            if (
              this.userDetails?.email ===
                this.myProfileForm.get(controlName).value ||
              (this.isEmailVerified &&
                this.verifiedEmailValue ===
                  this.myProfileForm.get(controlName).value)
            ) {
              this.isEmailVerified = '/images/success.svg';
            } else {
              this.isEmailVerifiable = true;
              this.isEmailVerified = '';
              this.verifiedEmailValue = '';
            }
          }

          controlName === 'username' && (this.isUserNameVerified = true);
        }),
      )
      .subscribe((resp) => {
        if (!resp?.message && controlName === 'email') return;
        if (resp?.message && controlName === 'email') {
          this.toaster.error(resp.message);
          const existingErrors =
            this.myProfileForm.get(controlName).errors || {};
          this.myProfileForm
            .get(controlName)
            .setErrors({ ...existingErrors, isExist: true });
          if (controlName === 'email') {
            if (this.myProfileForm.get('email').errors?.['isExist']) return;
          }
          return;
        }
        if (resp) {
          const existingErrors =
            this.myProfileForm.get(controlName).errors || {};
          this.myProfileForm
            .get(controlName)
            .setErrors({ ...existingErrors, isExist: true });
          if (controlName === 'email') {
            if (this.myProfileForm.get('email').errors?.['isExist']) return;
          }
          return;
        }
      });
  }

  onPrivacyPolicyClick(event: any) {
    event.preventDefault();
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`/${routes.home}/${routes.privacyPolicy}`]),
    );

    this.utilityService.openUrlInNewTab(url);
  }

  onTermsOfServiceClick(event: any) {
    event.preventDefault();
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`/${routes.home}/${routes.termsNCondition}`]),
    );

    this.utilityService.openUrlInNewTab(url, true);
  }

  onUpdateProfile() {
    const isAgreeInvalid = this.myProfileForm.get('isAgreeTnc')?.invalid;

    const hasOtherFieldsInvalid = Object.keys(this.myProfileForm.controls)
      .filter((key) => key !== 'isAgreeTnc')
      .some((key) => this.myProfileForm.get(key)?.invalid);

    if (hasOtherFieldsInvalid) {
      this.myProfileForm.markAllAsTouched();
      return;
    }

    if (!this.verifiedEmailValue) {
      this.toaster.error('Please verify your email before you proceed');
      return;
    }

    if (isAgreeInvalid) {
      this.toaster.error(
        'You must agree to the Terms of Use and Privacy Policy before proceeding.',
      );
      return;
    }

    const myProfileDetail = this.myProfileForm.getRawValue();
    const bodyObj = {
      username: myProfileDetail.username || '',
      fullname: myProfileDetail.fullName || '',
      emailId: myProfileDetail.email || '',
      phone: myProfileDetail.phone || '',
      userId: this.userDetails.id,
    };

    const uploadImageBody = {
      file_name: this.imageProfileData?.fileName || '',
      image_url: this.imageProfileData?.base64 || '',
      type: 'content',
    };

    const submitUserData = (profilePicUrl: string) => {
      const submitPayload = {
        ...bodyObj,
        profilePic: profilePicUrl,
      };
      return this.apiService.submitUserData(submitPayload);
    };

    const source$ = this.isProfilePicUploaded
      ? this.apiService.uploadImage(uploadImageBody).pipe(
          concatMap((resp) => {
            const url = resp?.url ? encodeURI(resp.url) : '';
            return submitUserData(url);
          }),
        )
      : submitUserData('');

    this.updateUserDataSub = source$.subscribe({
      next: (resp) => {
        this.toaster.success(
          resp?.header.msg || 'User information successfully updated.',
        );
        this.authService.saveUserProfile(resp.data);
        this.authService.redirectToDashboard();
      },
      error: apiErrorHandler.bind(this),
    });
  }

  onVerifyEmail() {
    const controlValue = this.myProfileForm.get('email')?.value;
    if (!controlValue) return;

    this.verifyEmail(controlValue);
  }

  verifyEmail(email: string, isResend: boolean = false) {
    this.apiService
      .sendVerificationEmail(email)
      .pipe()
      .subscribe({
        next: (res) => {
          this.toaster.success(res.message);
          if (!isResend) {
            this.matDialog
              .open(ConfirmationDialogActionComponent, {
                data: {
                  dialogIcon: 'images/verify-otp.svg',
                  iconAlt: 'Verify OTP',
                  iconTitle: 'Verify your email',
                  headerText: 'Verify your email',
                  message: this.sanitizer.bypassSecurityTrustHtml(
                    `<span style = "color : #475467">We've sent a code to <b>${email}</b></span>`,
                  ),
                  primaryButtonClass: 'btn-secondary',
                  primaryButtonText: 'Verify',
                  showOTPInput: true,
                  isRemarkMandatory: true,
                  viewAsinnerHTML: true,
                  submit: this.verifyEmail.bind(this),
                  isResendOTP: 'true',
                  isVerifyEmail: true,
                  email,
                },
              })
              .afterClosed()
              .subscribe((res) => {
                if (!res) return;
                this.verifiedEmailValue = email;
                this.updateVerificationStatus('email', true);
              });
          }
        },
        error: (error) => {
          this.toaster.error(error.message);
        },
      });
  }

  updateVerificationStatus(
    controlName: 'email' | 'phone' | 'username',
    isVerified: boolean,
  ) {
    if (controlName === 'email') {
      this.isEmailVerifiable = false;
      this.isEmailVerified = isVerified ? '/images/success.svg' : '';
    } else if (controlName === 'username') {
      this.isUserNameVerified = isVerified ? '/images/success.svg' : '';
    }
  }

  redirectToHome() {
    this.router.navigate(['/', routes.home], { replaceUrl: true });
  }

  ngOnDestroy() {
    this.updateUserDataSub?.unsubscribe();
  }
}
